import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { issueService } from '../services/issue.service';
import { oneOnOneService } from '../services/one-on-one.service';
import {
  CreateGoalApiRequest,
  CreateGoalApiResponse,
  IssueType,
  UpdateGoalApiRequest,
} from '@momentum/types';

// Query keys for goals
export const goalKeys = {
  all: ['goals'] as const,
  lists: () => [...goalKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...goalKeys.lists(), { filters }] as const,
  details: () => [...goalKeys.all, 'detail'] as const,
  detail: (id: string) => [...goalKeys.details(), id] as const,
};

export const useGoals = (
  userId: string | undefined,
  types: IssueType[] = [IssueType.GOAL]
) => {
  return useQuery({
    queryKey: [userId, JSON.stringify(types)],
    queryFn: () => issueService.getAllGoals(types),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
  });
};

export const useMyGoals = (
  userId: string | undefined,
  types: IssueType[] = [IssueType.GOAL]
) => {
  return useQuery({
    queryKey: goalKeys.list({ userId, types, scope: 'my-goals' }),
    queryFn: () => issueService.getMyGoals(types),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!userId,
  });
};

export const useCreateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGoalApiRequest): Promise<CreateGoalApiResponse> =>
      issueService.createGoal(data),
    onSuccess: () => {
      // Invalidate company hierarchy queries to refresh goals tree
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      // Also invalidate goal lists for other views
      queryClient.invalidateQueries({ queryKey: goalKeys.lists() });
    },
  });
};

export const useUpdateGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateGoalApiRequest;
    }): Promise<CreateGoalApiResponse> => issueService.updateGoal(id, data),
    onSuccess: () => {
      // Invalidate company hierarchy queries to refresh goals tree
      queryClient.invalidateQueries({ queryKey: ['company-hierarchy'] });
      // Also invalidate goal lists for other views
      queryClient.invalidateQueries({ queryKey: goalKeys.lists() });
      // Invalidate user-assigned-goals for one-on-one review
      queryClient.invalidateQueries({ queryKey: ['user-assigned-goals'] });
    },
  });
};

export const useUserAssignedGoals = (
  employeeId: string,
  meetingType?: string,
  managerId?: string
) => {
  const hasAllParams = !!employeeId && !!meetingType && !!managerId;

  return useQuery({
    queryKey: ['user-assigned-goals', employeeId, meetingType, managerId],
    queryFn: () => {
      if (!meetingType || !managerId) {
        throw new Error('Missing required parameters');
      }

      return oneOnOneService.getUserAssignedGoals(
        employeeId,
        meetingType,
        managerId
      );
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: hasAllParams,
  });
};
